import React, { useEffect, useState } from 'react';
import { UmbrellaIcon } from 'lucide-react';
import { WallCard } from './WallCard';
import { RainBand } from './RainBand';
import { RouteMap } from './RouteMap';
import { AgentMark } from '../../ui/AgentMark';
import { useDb } from '../../../hooks/useDb';
import { usePingOn } from '../../../hooks/usePingOn';
import { agendaToday, contactById, mailsFrom, nextEvent, nightSummary, tripFor } from '../../../utils/dbSelectors';
import { durationLabel, toMinutes } from '../../../utils/time';

/** Agenda as a list — used on narrow screens; on the wall the agenda is an arc around Anna. */
export function VandaagCard() {
  const {
    events,
    next,
    now
  } = useDb(t => ({
    events: agendaToday(t),
    next: nextEvent(t),
    now: toMinutes(t.meta.time)
  }));
  usePingOn(now);
  return <WallCard agent="agenda" title="Vandaag" where="lokaal" traceId="agenda-vandaag" data-id="element-1498" data-mp="1kwse6p8nnld8">
      <ol className="space-y-3" data-id="element-1499" data-mp="1fxflhnv3rk2q">
        {events.map(row => {
        const isNext = row.id === next?.id;
        return <li key={row.id} className="flex gap-4" data-id="element-1500" data-mp="1zoicutiuuaxt">
              <span className={`w-16 shrink-0 tabular-nums ${isNext ? 'font-semibold text-ink' : 'text-ink-muted'}`} data-id="element-1501" data-mp="23qwvbcim38ok">{row.start}</span>
              <div className="min-w-0" data-id="element-1502" data-mp="1ps5l416ve75p">
                <p className={isNext ? 'font-semibold text-ink' : ''} data-id="element-1503" data-mp="1pt8bm80a67l3">{row.title}</p>
                {isNext && <p className="text-accent" data-id="element-1504" data-mp="1usb4ku2zkncp">over {durationLabel(toMinutes(row.start) - now)}</p>}
              </div>
            </li>;
      })}
      </ol>
    </WallCard>;
}
export function VannachtCard() {
  const rows = useDb(nightSummary);
  return <WallCard agent="initiatief" kicker="terwijl je sliep" title="Vannacht gedaan" level="zelf" where="lokaal" traceId="nacht-build" data-id="element-1505" data-mp="28oyfy1vorg25">
      <ul className="space-y-3" data-id="element-1506" data-mp="2dmzvwdxtez9e">
        {rows.map(row => <li key={row.id} className="flex items-start gap-3" data-id="element-1507" data-mp="31dm17xgfq5fp">
            {row.agent !== 'systeem' && <span className="mt-0.5" data-id="element-1508" data-mp="3665e0s7aswjg">
                <AgentMark agentId={row.agent} size="sm" data-id="element-1509" data-mp="3g1cdc87cpd5x" />
              </span>}
            <span className="text-ink" data-id="element-1510" data-mp="3fyzza64prmy5">{row.summary}</span>
          </li>)}
      </ul>
    </WallCard>;
}
const BREW_SECONDS = 180;
const RING = 2 * Math.PI * 26;
export function KoffieCard() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = window.setInterval(() => setElapsed(Math.min(BREW_SECONDS, (Date.now() - start) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, []);
  const left = Math.ceil((BREW_SECONDS - elapsed) / 60);
  const done = elapsed >= BREW_SECONDS;
  usePingOn(done ? 'klaar' : left);
  return <WallCard agent="huis" title={done ? 'Koffie is klaar' : 'Koffie staat aan'} level="zelf" where="lokaal" traceId="koffie" data-id="element-1511" data-mp="2idlt99x7tdsu">
      <div className="flex items-center gap-4" data-id="element-1512" data-mp="2ec9zqtb808fd">
        <svg viewBox="0 0 60 60" className="h-14 w-14 shrink-0 -rotate-90" aria-hidden data-id="element-1513" data-mp="2ee37kbxkkyoy">
          <circle cx="30" cy="30" r="26" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="5" data-id="element-1514" data-mp="29dpa1ntlh756" />
          <circle cx="30" cy="30" r="26" fill="none" stroke="#A8D982" strokeWidth="5" strokeLinecap="round" strokeDasharray={RING} strokeDashoffset={RING * (1 - Math.max(0.04, elapsed / BREW_SECONDS))} style={{
          transition: 'stroke-dashoffset 1s linear'
        }} data-id="element-1515" data-mp="ouliyqxemljr" />
        </svg>
        <p className="text-ink" data-id="element-1516" data-mp="6684ny83k7cx">{done ? 'Staat voor je klaar.' : `Klaar over ${left} min`}</p>
      </div>
    </WallCard>;
}
export function WeerCard() {
  const hours = useDb(t => t.weather.filter(w => toMinutes(w.time) >= 12 * 60 && toMinutes(w.time) <= 16 * 60));
  const firstRain = hours.find(h => h.mm > 0);
  return <WallCard agent="initiatief" kicker="weer" title={firstRain ? `Regen vanaf ${firstRain.time}` : 'Droog'} where="cloud" traceId="weer" feedback data-id="element-1517" data-mp="3im1zlxpj1u11">
      <RainBand hours={hours} data-id="element-1518" data-mp="3njajq3u8k84x" />
      {firstRain && <p className="mt-3 flex items-center gap-2.5 text-ink" data-id="element-1519" data-mp="1u1wfj1q0jsm3">
          <UmbrellaIcon className="h-6 w-6 shrink-0 text-ink-muted" aria-hidden data-id="element-1520" data-mp="1u01vdpmkg4j2" /> Neem een paraplu mee
        </p>}
    </WallCard>;
}
export function RouteCard() {
  const trip = useDb(t => tripFor(t, 'e-lisa'));
  if (!trip) return null;
  const destination = trip.event.location?.split(',')[0] ?? 'Lisa';
  return <WallCard agent="agenda" kicker="route" title={`Vertrek om ${trip.depart}`} where="cloud" traceId="route" feedback data-id="element-1521" data-mp="2csdum3hxapoz">
      <RouteMap destination={destination} data-id="element-1522" data-mp="27ucargcoamcg" />
      <p className="mt-3" data-id="element-1523" data-mp="27vng3klro25o">
        {trip.minutes} min met de auto · aankomst {trip.arrive}
      </p>
    </WallCard>;
}
export function LisaCard() {
  const {
    lisa,
    mail
  } = useDb(t => ({
    lisa: contactById(t, 'lisa'),
    mail: mailsFrom(t, 'lisa').slice(-1)[0]
  }));
  if (!lisa) return null;
  return <WallCard agent="initiatief" kicker={lisa.role} title={`Voorbereiding ${lisa.name}`} where="lokaal" traceId="meeting-lisa" feedback data-id="element-1524" data-mp="rhdtow134nhh">
      <ul className="space-y-1 text-ink" data-id="element-1525" data-mp="d9969jyz4asf">
        {lisa.about.map(line => <li key={line} data-id="element-1526" data-mp="hbcrh0bg2tqw">{line}</li>)}
      </ul>
      {mail && <p className="mt-3" data-id="element-1527" data-mp="hdgulg1iroq4">
          Laatste mail: “{mail.subject}” · {mail.received}
        </p>}
      {lisa.openQuestion && <p className="mt-3 text-ink" data-id="element-1528" data-mp="8iiyk032rvuk">
          <span className="text-ink-muted" data-id="element-1529" data-mp="8hhmpte8j5bs">Open vraag: </span>
          {lisa.openQuestion}
        </p>}
    </WallCard>;
}